import { supabase_guest } from "../supabase/supabaseClient";
import {
  uploadFileToCloudflareR2,
  deleteFileFromCloudflareR2,
} from "../utils/supabaseUtils";

export const registerGuests = async (formData) => {
  const results = [];
  const eventId = formData.get("eventId");
  const guestsMap = {};

  for (const [key, value] of formData.entries()) {
    const match = key.match(/^guests\[(\d+)\]\[(.+)\]$/);
    if (!match) continue;

    const index = match[1];
    const field = match[2];

    if (!guestsMap[index]) guestsMap[index] = {};
    guestsMap[index][field] = value;
  }

  const guests = Object.values(guestsMap);

  const normalizePhone = (phone) => {
    let p = String(phone || "").replace(/\D/g, "");

    if (p.startsWith("62")) {
      p = "0" + p.slice(2);
    }

    return p;
  };

  for (const g of guests) {
    const normalizedPhone = normalizePhone(g.phone);
    let qrUrl = null;
    let proofOfDonationUrl = null;
    let proofOfDonationObjectName = null;
    let proofOfSponsorUrl = null;
    let proofOfSponsorObjectName = null;

    try {
      if (g.isDonate === "true" && g.proofOfDonationFile instanceof File) {
        const uploadResult = await uploadFileToCloudflareR2(
          g.proofOfDonationFile,
          g.full_name,
          `event_id_${eventId}/dana`
        );

        proofOfDonationUrl = uploadResult?.publicUrl ?? null;
        proofOfDonationObjectName = uploadResult?.objectName ?? null;

        if (!proofOfDonationUrl) {
          throw new Error("Upload bukti dana gagal");
        }
      }

      if (g.isSponsor === "true" && g.sponsorProofFile instanceof File) {
        const uploadResult = await uploadFileToCloudflareR2(
          g.sponsorProofFile,
          g.full_name,
          `event_id_${eventId}/sponsor`
        );

        proofOfSponsorUrl = uploadResult?.publicUrl ?? null;
        proofOfSponsorObjectName = uploadResult?.objectName ?? null;

        if (!proofOfSponsorUrl) {
          throw new Error("Upload bukti sponsor gagal");
        }
      }

      const basePayload = {
        p_created_by: g.full_name?.trim(),
        p_event_id: eventId,
        p_full_name: g.full_name?.trim(),
        p_phone: normalizedPhone,
        p_guest_type_id: null,
      };

      const payloadWithGuestType = { ...basePayload };
      const payloadWithoutGuestType = {
        ...basePayload,
        p_guest_type_id: undefined,
      };

      let data = null;
      let error = null;

      try {
        const response = await supabase_guest.rpc("register_guest_fix", payloadWithGuestType);
        data = response.data;
        error = response.error;
      } catch (rpcError) {
        error = rpcError;
      }

      if (error?.code === "PGRST202" || error?.message?.includes("Could not find the function")) {
        const fallbackResponse = await supabase_guest.rpc("register_guest_fix", payloadWithoutGuestType);
        data = fallbackResponse.data;
        error = fallbackResponse.error;
      }

      if (error) {
        if (proofOfDonationObjectName) {
          await deleteFileFromCloudflareR2(proofOfDonationObjectName);
        }
        if (proofOfSponsorObjectName) {
          await deleteFileFromCloudflareR2(proofOfSponsorObjectName);
        }

        results.push({
          name: g.full_name,
          phone: g.phone,
          seat: "-",
          qr_url: null,
          status: "gagal",
          reason: error.message,
        });
        continue;
      }

      const res = Array.isArray(data) ? data[0] ?? {} : data ?? {};

      // HANDLE ALL STATUS SAFELY
      if (res.status !== "success") {
        if (proofOfDonationObjectName) {
          await deleteFileFromCloudflareR2(proofOfDonationObjectName);
        }
        if (proofOfSponsorObjectName) {
          await deleteFileFromCloudflareR2(proofOfSponsorObjectName);
        }

        results.push({
          name: g.full_name,
          phone: g.phone,
          seat: "-",
          cluster_code: "-",
          cluster_id: null,
          qr_url: null,
          status: res.status ?? "gagal",
          reason: res.reason ?? null,
        });
        continue;
      }

      // QR GENERATION
      try {
        console.log("🎨 Generating QR for attendance_id:", res.attendance_id);
        const { data: qrData } = await supabase_guest.functions.invoke(
          "generate-qr",
          {
            body: {
              event_id: eventId,
              attendance_id: res.attendance_id,
            },
          }
        );

        console.log("✅ QR Generated:", qrData);
        qrUrl = qrData?.qr_url ?? null;
      } catch (e) {
        console.error("❌ QR error:", e);
      }

      // WA ONLY IF QR EXISTS
      if (qrUrl) {
        try {
          console.log("📱 Sending WhatsApp to:", {
            phone: normalizePhone(g.phone),
            name: g.full_name,
            seat: res.seat_number,
          });
          await supabase_guest.functions.invoke("send-wa", {
            body: {
              phone: normalizePhone(g.phone),
              name: g.full_name,
              seat: res.seat_number,
              cluster_code: res.cluster_code,
              qr_url: qrUrl,
              event_id: eventId,
            },
          });
          console.log("✅ WhatsApp sent successfully");
        } catch (waErr) {
          console.error("❌ WA error:", waErr);
        }
      }

      results.push({
        name: g.full_name,
        phone: g.phone,
        seat: res.seat_number ?? "-",
        cluster_code: res.cluster_code ?? "-",
        cluster_id: res.cluster_id ?? null,
        attendance_id: res.attendance_id ?? null,
        guest_id: res.guest_id ?? null,
        qr_url: qrUrl,
        status: "berhasil",
        reason: null,
      });
    } catch (err) {
      console.error("💥 Unexpected error processing guest:", {
        guestName: g.full_name,
        guestPhone: g.phone,
        error: err?.message,
        stack: err?.stack,
      });
      if (proofOfDonationObjectName) {
        await deleteFileFromCloudflareR2(proofOfDonationObjectName);
      }
      if (proofOfSponsorObjectName) {
        await deleteFileFromCloudflareR2(proofOfSponsorObjectName);
      }

      results.push({
        name: g.full_name,
        phone: g.phone,
        seat: "-",
        qr_url: null,
        status: "gagal",
        reason: err?.message ?? "unknown error",
      });
    }
  }

  return results;
};
